/**
 * エリア比較ダッシュボード (docs/25 §10-5) 用の、選択した複数エリアの範囲を地図で比較表示する
 * コンポーネント (2026-07-21 追加、同日にスモールマルチプル方式へ変更)。
 *
 * 当初は全エリアの多角形を1枚の地図に重ねる実装だったが、選択したエリア同士が地理的に
 * 大きく離れている場合 (例: 別地区の2エリアを比較) に `fitBounds` が両方を含めようとして
 * 過度にズームアウトし、各エリアの形が見えなくなる問題が判明した (ユーザー指摘)。
 * そのため「エリアごとに1枚、そのエリアだけにフィットした地図」を並べるスモールマルチプル方式に
 * 変更した — 各地図は独立してズームされるため、離れたエリア同士でも両方とも見やすい形で表示される。
 *
 * `AreaReportPreviewMap.tsx` (単一エリア + 地点のドーナツ表示、PDFキャプチャ対応) とは別実装 —
 * 本コンポーネントは地点・キャプチャ機能を持たず、エリアの多角形を表示するだけの単純な地図。
 */
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { type Map as MaplibreMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';

const POLYGON_SOURCE_ID = 'area-comparison-polygon';
const FILL_LAYER_ID = 'area-comparison-polygon-fill';
const OUTLINE_LAYER_ID = 'area-comparison-polygon-outline';

export interface AreaComparisonMapArea {
  id: string;
  name: string;
  polygon: { latitude: number; longitude: number }[];
  color: string;
}

export interface AreaComparisonMapProps {
  areas: AreaComparisonMapArea[];
  className?: string;
}

/** 1エリア分の独立した地図 (自身の多角形だけに fitBounds する)。 */
function AreaMiniMap({ area }: { area: AreaComparisonMapArea }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          gsi: {
            type: 'raster',
            tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 18,
            attribution: '地理院タイル',
          },
        },
        layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
      },
      center: [139.767, 35.681],
      zoom: 12,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => setReady(true));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (area.polygon.length < 3) return;

    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                ...area.polygon.map((c) => [c.longitude, c.latitude]),
                [area.polygon[0]!.longitude, area.polygon[0]!.latitude],
              ],
            ],
          },
        },
      ],
    };

    if (!map.getSource(POLYGON_SOURCE_ID)) {
      map.addSource(POLYGON_SOURCE_ID, { type: 'geojson', data: fc });
      map.addLayer({
        id: FILL_LAYER_ID,
        type: 'fill',
        source: POLYGON_SOURCE_ID,
        paint: { 'fill-color': area.color, 'fill-opacity': 0.22 },
      });
      map.addLayer({
        id: OUTLINE_LAYER_ID,
        type: 'line',
        source: POLYGON_SOURCE_ID,
        paint: { 'line-color': area.color, 'line-width': 2 },
      });
    } else {
      (map.getSource(POLYGON_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
      map.setPaintProperty(FILL_LAYER_ID, 'fill-color', area.color);
      map.setPaintProperty(OUTLINE_LAYER_ID, 'line-color', area.color);
    }

    const bounds = new maplibregl.LngLatBounds();
    for (const c of area.polygon) bounds.extend([c.longitude, c.latitude]);
    map.fitBounds(bounds, { padding: 24, maxZoom: 18, duration: 0 });
  }, [ready, area]);

  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: area.color }}
        />
        {area.name}
      </p>
      <div ref={containerRef} className="w-full h-48 rounded border border-slate-200" />
    </div>
  );
}

export function AreaComparisonMap({ areas, className }: AreaComparisonMapProps) {
  const validAreas = areas.filter((a) => a.polygon.length >= 3);
  if (validAreas.length === 0) {
    return <p className="text-sm text-slate-400">表示できるエリア範囲がありません。</p>;
  }
  return (
    <div className={className ?? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
      {validAreas.map((a) => (
        <AreaMiniMap key={a.id} area={a} />
      ))}
    </div>
  );
}
